import { Phone, CheckCircle, ShieldCheck, Accessibility, CreditCard, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { HeroBackground } from "@/components/HeroBackground";
import roadVideo from "@/assets/hero-road.mp4.asset.json";
import roadVideoWebm from "@/assets/hero-road.webm.asset.json";
import roadPoster from "@/assets/hero-road-poster.webp.asset.json";

const Hero = () => {
  return (
    <div className="relative">
      <section className="relative bg-white pt-14 sm:pt-20 md:pt-32 pb-28 sm:pb-36 md:pb-44 overflow-hidden">
        <HeroBackground gradient={false} />


        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-foreground mb-4 sm:mb-6 leading-tight tracking-tight">
              Non-Emergency Medical Transportation in Portland
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-foreground/70 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed font-medium">
              Safe, reliable non-emergency medical transportation and wheelchair transportation service for medical appointments, hospitals, and assisted living facilities.
            </p>


            <div className="flex flex-col items-center sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12 px-6 sm:px-4 w-full max-w-sm sm:max-w-none mx-auto">
              <Button asChild size="lg" className="w-full sm:w-auto text-base sm:text-lg h-14 bg-button text-button-foreground hover:bg-button/90 shadow-lg transition-all duration-300 rounded-xl">
                <a href="tel:9713445446" className="flex items-center justify-center w-full">
                  <span className="inline-flex items-center gap-2">
                    <Phone className="h-5 w-5 shrink-0" />
                    <span>Call or Text</span>
                  </span>
                </a>
              </Button>
              <Button asChild size="lg" className="w-full sm:w-auto text-base sm:text-lg h-14 bg-button text-button-foreground hover:bg-button/90 shadow-lg transition-all duration-300 rounded-xl">
                <Link to="/contact#booking-form" className="flex items-center justify-center w-full">
                  <span className="inline-flex items-center gap-2">
                    <Calculator className="h-5 w-5 shrink-0" />
                    <span>Get Instant Quote</span>
                  </span>
                </Link>
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-5">
              <div className="flex items-center bg-card px-3 sm:px-4 py-2 sm:py-2.5 rounded-full shadow-sm border border-border">
                <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                <span className="font-medium text-foreground text-xs sm:text-sm md:text-base">Licensed</span>
              </div>
              <div className="flex items-center bg-card px-3 sm:px-4 py-2 sm:py-2.5 rounded-full shadow-sm border border-border">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                <span className="font-medium text-foreground text-xs sm:text-sm md:text-base">Insured</span>
              </div>
              <div className="flex items-center bg-card px-3 sm:px-4 py-2 sm:py-2.5 rounded-full shadow-sm border border-border">
                <Accessibility className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                <span className="font-medium text-foreground text-xs sm:text-sm md:text-base">Wheelchair Accessible</span>
              </div>
              <div className="flex items-center bg-card px-3 sm:px-4 py-2 sm:py-2.5 rounded-full shadow-sm border border-border">
                <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                <span className="font-medium text-foreground text-xs sm:text-sm md:text-base">Private Pay</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animated road band on a white background — replaces the static wave */}
      <div className="relative bg-white -mt-20 sm:-mt-28 md:-mt-36 pointer-events-none overflow-hidden">
        <video
          poster={roadPoster.url}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          disablePictureInPicture
          tabIndex={-1}
          aria-hidden="true"
          style={{ filter: "brightness(1.008)" }}
          width="1920"
          height="1080"
          className="w-full h-auto"
        >
          <source src={roadVideoWebm.url} type="video/webm" />
          <source src={roadVideo.url} type="video/mp4" />
        </video>
      </div>
    </div>);

};

export default Hero;